import { useEffect, useRef, useState } from 'react';
import { CONTACT_CREATED_RANGE_MESSAGE } from '@brimo/shared';
import { Button, Eyebrow, FilterChip, FormRow, Popover, TextField } from '../../ds/components';
import {
  entryRangeLabel,
  entryRangePresetLabels,
  entryRangePresets,
  matchingPreset,
  presetRange,
  type EntryRange,
} from './list-controls';
import styles from './EntryRangeFilter.module.css';

export interface EntryRangeFilterProps {
  value: EntryRange;
  onChange: (next: EntryRange) => void;
}

const EMPTY: EntryRange = { from: '', until: '' };

/**
 * „Zeitraum“ chip of the contacts list (Nico feedback FB-003, D-322): presets „Heute“, „7 Tage“, „30 Tage“,
 * „Dieses Quartal“ apply at once; „Von“/„Bis“ apply with „Übernehmen“. The days are Berlin calendar days.
 */
export function EntryRangeFilter({ value, onChange }: EntryRangeFilterProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [until, setUntil] = useState(value.until);
  const active = value.from !== '' || value.until !== '';
  const current = matchingPreset(value);
  const invalid = from !== '' && until !== '' && until < from;

  useEffect(() => {
    if (!open) return;
    setFrom(value.from);
    setUntil(value.until);
  }, [open, value.from, value.until]);

  function apply(next: EntryRange) {
    onChange(next);
    setOpen(false);
  }

  return (
    <>
      <span ref={anchorRef} className={styles.anchor}>
        <FilterChip
          label={entryRangeLabel(value)}
          active={active}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((state) => !state)}
          onRemove={active ? () => apply(EMPTY) : undefined}
        />
      </span>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-start"
        offset={6}
        width={360}
        variant="panel"
        label="Zeitraum"
      >
        <div className={styles.body}>
          <Eyebrow size={11}>Eingang im Zeitraum</Eyebrow>
          <div className={styles.presets} role="group" aria-label="Schnellauswahl">
            {entryRangePresets.map((preset) => (
              <FilterChip
                key={preset}
                label={entryRangePresetLabels[preset]}
                active={current === preset}
                onClick={() => apply(presetRange(preset))}
              />
            ))}
          </div>
          <FormRow columns={2}>
            <TextField
              label="Von"
              type="date"
              size="compact"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <TextField
              label="Bis"
              type="date"
              size="compact"
              value={until}
              error={invalid ? CONTACT_CREATED_RANGE_MESSAGE : undefined}
              onChange={(event) => setUntil(event.target.value)}
            />
          </FormRow>
          <div className={styles.footer}>
            <Button variant="secondary" size="small" onClick={() => apply(EMPTY)}>
              Filter entfernen
            </Button>
            <Button size="small" disabled={invalid} onClick={() => apply({ from, until })}>
              Übernehmen
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
